import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useOrgPermissions } from "@/hooks/useOrgPermissions";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useState } from "react";
import {
  Users, TrendingUp, PenSquare, Heart, Eye, MessageCircle,
  Trophy, BarChart3, Download, AtSign,
} from "lucide-react";
import { Facebook, Instagram, Linkedin, Twitter } from "lucide-react";
import { formatDistanceToNow } from "date-fns";

const PLATFORM_ICONS: Record<string, React.ElementType> = {
  instagram: Instagram, twitter: Twitter, linkedin: Linkedin, facebook: Facebook, threads: AtSign,
};

function formatK(n: number) {
  if (!n) return "0";
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000)     return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
}

export function TeamPerformanceReport() {
  const { orgId } = useOrgPermissions();
  const [range, setRange] = useState("30");

  const { data: members = [] } = useQuery({
    queryKey: ["team-members-perf", orgId],
    enabled: !!orgId,
    queryFn: async () => {
      const { data } = await (supabase as any)
        .from("organization_members")
        .select("user_id, role_id, roles(name), profiles(id, full_name, avatar_url, email)")
        .eq("org_id", orgId!);
      return (data ?? []) as any[];
    },
  });

  const { data: posts = [] } = useQuery({
    queryKey: ["team-posts-perf", orgId, range],
    enabled: !!orgId,
    queryFn: async () => {
      const since = new Date(Date.now() - Number(range) * 86400000).toISOString();
      const { data } = await (supabase as any)
        .from("posts")
        .select("id,user_id,status,platform,content,created_at,published_at,post_analytics(likes_count,comments_count,shares_count,reach_count,impressions_count,engagement_rate)")
        .eq("org_id", orgId!)
        .gte("created_at", since)
        .order("created_at", { ascending: false });
      return (data ?? []) as any[];
    },
  });

  // Aggregate stats per user
  const statsByUser: Record<string, {
    userId: string; name: string; avatar: string; role: string;
    total: number; published: number; scheduled: number; draft: number; pending: number;
    likes: number; comments: number; reach: number; impressions: number; engagement: number;
    platforms: Set<string>;
  }> = {};

  for (const m of members) {
    const profile = m.profiles ?? {};
    statsByUser[m.user_id] = {
      userId: m.user_id,
      name: profile.full_name ?? profile.email ?? "Unknown",
      avatar: profile.avatar_url ?? "",
      role: m.roles?.name ?? "member",
      total: 0, published: 0, scheduled: 0, draft: 0, pending: 0,
      likes: 0, comments: 0, reach: 0, impressions: 0, engagement: 0,
      platforms: new Set(),
    };
  }

  for (const post of posts) {
    if (!statsByUser[post.user_id]) continue;
    const s = statsByUser[post.user_id];
    s.total++;
    if (post.status === "published") s.published++;
    else if (post.status === "scheduled") s.scheduled++;
    else if (post.status === "draft") s.draft++;
    else if (post.status === "pending_approval") s.pending++;
    if (post.platform) s.platforms.add(post.platform);
    const a = post.post_analytics;
    if (a) {
      s.likes       += a.likes_count ?? 0;
      s.comments    += a.comments_count ?? 0;
      s.reach       += a.reach_count ?? 0;
      s.impressions += a.impressions_count ?? 0;
    }
  }

  const ranked = Object.values(statsByUser)
    .filter(s => s.total > 0)
    .sort((a, b) => b.published - a.published);

  const allUsers = Object.values(statsByUser);
  const totalPosts     = posts.length;
  const totalPublished = posts.filter(p => p.status === "published").length;
  const totalLikes     = Object.values(statsByUser).reduce((s, u) => s + u.likes, 0);
  const totalReach     = Object.values(statsByUser).reduce((s, u) => s + u.reach, 0);

  const exportCSV = () => {
    const rows = [
      ["Member","Role","Posts","Published","Scheduled","Draft","Likes","Comments","Reach","Impressions"],
      ...ranked.map(s => [s.name, s.role, s.total, s.published, s.scheduled, s.draft, s.likes, s.comments, s.reach, s.impressions]),
    ];
    const csv = rows.map(r => r.join(",")).join("\n");
    const a   = document.createElement("a");
    a.href    = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    a.download = `team-performance-${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold flex items-center gap-2">
            <Users className="w-5 h-5 text-primary" /> Team Performance
          </h2>
          <p className="text-sm text-muted-foreground">See how each team member is contributing to social media</p>
        </div>
        <div className="flex gap-2">
          <Select value={range} onValueChange={setRange}>
            <SelectTrigger className="w-32 h-8 text-sm"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="90">Last 90 days</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={exportCSV}>
            <Download className="w-3.5 h-3.5 mr-1.5" /> Export
          </Button>
        </div>
      </div>

      {/* Team totals */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: "Total Posts",  value: totalPosts,     icon: PenSquare,  color: "text-blue-500",   bg: "bg-blue-50" },
          { label: "Published",    value: totalPublished, icon: TrendingUp, color: "text-green-500",  bg: "bg-green-50" },
          { label: "Total Likes",  value: formatK(totalLikes),  icon: Heart,     color: "text-rose-500",  bg: "bg-rose-50" },
          { label: "Total Reach",  value: formatK(totalReach),  icon: Eye,       color: "text-purple-500",bg: "bg-purple-50" },
        ].map(stat => (
          <Card key={stat.label} className="p-4">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs text-muted-foreground">{stat.label}</p>
                <p className="text-2xl font-bold mt-1">{stat.value}</p>
              </div>
              <div className={`w-8 h-8 rounded-lg ${stat.bg} flex items-center justify-center`}>
                <stat.icon className={`w-4 h-4 ${stat.color}`} />
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* Leaderboard */}
      {ranked.length === 0 ? (
        <Card className="p-10 text-center">
          <Users className="w-10 h-10 mx-auto mb-3 text-muted-foreground/30" />
          <p className="font-medium text-muted-foreground">No team activity yet</p>
          <p className="text-sm text-muted-foreground mt-1">Posts created by team members will appear here.</p>
        </Card>
      ) : (
        <Card className="p-5">
          <h3 className="font-semibold text-sm mb-4 flex items-center gap-2">
            <Trophy className="w-4 h-4 text-amber-500" /> Leaderboard
          </h3>
          <div className="space-y-3">
            {ranked.map((s, i) => {
              const engRate = s.reach > 0 ? ((s.likes + s.comments) / s.reach * 100).toFixed(1) : "—";
              return (
                <div key={s.userId} className="flex items-center gap-4 p-3 rounded-xl border border-border hover:bg-muted/30 transition-colors">
                  <span className={`text-base font-bold w-6 text-center shrink-0 ${i === 0 ? "text-amber-500" : i === 1 ? "text-slate-400" : i === 2 ? "text-amber-700" : "text-muted-foreground"}`}>
                    {i === 0 ? "🥇" : i === 1 ? "🥈" : i === 2 ? "🥉" : `#${i+1}`}
                  </span>

                  <div className="w-9 h-9 rounded-full bg-primary/20 flex items-center justify-center shrink-0 text-sm font-bold text-primary overflow-hidden">
                    {s.avatar ? <img src={s.avatar} alt={s.name} className="w-full h-full object-cover" /> : s.name.charAt(0).toUpperCase()}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-semibold text-sm truncate">{s.name}</p>
                      <Badge variant="secondary" className="text-[10px] capitalize">{s.role}</Badge>
                    </div>
                    <div className="flex items-center gap-1 mt-0.5">
                      {[...s.platforms].map(p => {
                        const Icon = PLATFORM_ICONS[p] ?? AtSign;
                        return <Icon key={p} className="w-3 h-3 text-muted-foreground" />;
                      })}
                    </div>
                  </div>

                  <div className="grid grid-cols-4 gap-4 text-center shrink-0">
                    {[
                      { label: "Posts",      value: s.total },
                      { label: "Published",  value: s.published },
                      { label: "Likes",      value: formatK(s.likes) },
                      { label: "Eng. Rate",  value: `${engRate}%` },
                    ].map(col => (
                      <div key={col.label} className="min-w-[48px]">
                        <p className="text-sm font-bold">{col.value}</p>
                        <p className="text-[10px] text-muted-foreground">{col.label}</p>
                      </div>
                    ))}
                  </div>

                  {/* Mini progress bar: posts share */}
                  <div className="w-16 shrink-0">
                    <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                      <div
                        className="h-full bg-primary rounded-full"
                        style={{ width: `${totalPosts ? Math.round((s.total / totalPosts) * 100) : 0}%` }}
                      />
                    </div>
                    <p className="text-[10px] text-muted-foreground mt-0.5 text-right">
                      {totalPosts ? Math.round((s.total / totalPosts) * 100) : 0}% share
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {/* Members with no posts */}
      {allUsers.filter(u => u.total === 0).length > 0 && (
        <Card className="p-5">
          <h3 className="font-semibold text-sm mb-3 text-muted-foreground">Inactive members (no posts in period)</h3>
          <div className="flex flex-wrap gap-2">
            {allUsers.filter(u => u.total === 0).map(u => (
              <div key={u.userId} className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-muted/50 border border-border">
                <div className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center text-xs font-bold text-primary">
                  {u.name.charAt(0).toUpperCase()}
                </div>
                <span className="text-xs text-muted-foreground">{u.name}</span>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}
